import Typography from '@navi/web-ui/lib/primitives/Typography';
import React, { useCallback, useEffect, useState } from 'react';
import cx from 'classnames';
import './CircularProgress.scss';

interface ICircularProgress {
  sqSize: number;
  percentage: number;
  strokeWidth: number;
  withAnimation: boolean;
  onStep?: (progress: number) => void;
  showPercentage?: boolean;
  circleBackground?: string;
  circleProgress?: string;
  defaultCircleOffsets?: { dashArray: number; dashOffset: number };
}

const CircularSvg: React.FC<ICircularProgress> = props => {
  const {
    percentage,
    sqSize,
    strokeWidth,
    withAnimation = false,
    onStep = () => {},
    circleBackground,
    circleProgress,
    defaultCircleOffsets = { dashArray: 0, dashOffset: 0 }
  } = props;
  const halfsqSize = sqSize / 2;
  const viewBox = `0 0 ${sqSize} ${sqSize}`;
  const radius = (sqSize - strokeWidth) / 2;
  const dashArray = radius * Math.PI * 2;
  const [circleOffsets, setCircleOffsets] = useState(defaultCircleOffsets);

  useEffect(() => {
    const computeAndSetPercentage = (
      percentage: number,
      dashArray: number,
      onStep?: (e: number) => void
    ) => {
      const _dashOffset = dashArray - (dashArray * percentage) / 100;
      setCircleOffsets({
        dashArray,
        dashOffset: _dashOffset
      });
      if (typeof onStep === 'function') onStep(percentage);
    };
    if (!withAnimation) {
      computeAndSetPercentage(percentage, dashArray, onStep);
      return;
    }

    const animationDuration = 300;
    const framesPerSecond = animationDuration / percentage;
    let progress = 0;

    // TODO:: Explore requestAnimationFrame instead of setInterval
    const timer = setInterval(() => {
      if (progress < percentage - 1) {
        progress += 1;
      } else {
        progress = percentage;
        clearInterval(timer);
      }
      computeAndSetPercentage(progress, dashArray, onStep);
    }, framesPerSecond);

    return () => {
      clearInterval(timer);
    };
  }, [percentage]);

  return (
    <svg width={sqSize} height={sqSize} viewBox={viewBox}>
      <circle
        className={cx('circle-background', circleBackground)}
        cx={halfsqSize}
        cy={halfsqSize}
        r={radius}
        strokeWidth={`${strokeWidth}px`}
      />
      <circle
        className={cx('circle-progress', circleProgress)}
        cx={halfsqSize}
        cy={halfsqSize}
        r={radius}
        strokeWidth={`${strokeWidth}px`}
        // Start progress marker at 12 O'Clock
        transform={`rotate(-90 ${halfsqSize} ${halfsqSize})`}
        style={{
          strokeDasharray: circleOffsets.dashArray,
          strokeDashoffset: circleOffsets.dashOffset
        }}
      />
    </svg>
  );
};

const CircularProgress: React.FC<ICircularProgress> = props => {
  const {
    percentage,
    sqSize,
    strokeWidth,
    withAnimation = false,
    showPercentage,
    circleBackground,
    circleProgress,
    defaultCircleOffsets
  } = props;
  const [progress, setProgress] = useState(0);

  const handleStep = useCallback((e: number) => {
    setProgress(e);
  }, []);
  return (
    <>
      <CircularSvg
        sqSize={sqSize}
        percentage={percentage}
        strokeWidth={strokeWidth}
        withAnimation={withAnimation}
        onStep={handleStep}
        circleBackground={circleBackground}
        circleProgress={circleProgress}
        defaultCircleOffsets={defaultCircleOffsets}
      />
      {showPercentage && (
        <Typography className="circle-text" variant="h6">
          {progress}%
        </Typography>
      )}
    </>
  );
};

export default CircularProgress;
